/**
 * Address label stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const addressLabelStickersPack: ToilePack = {
    slug: 'address-label-stickers',
    category: 'sticker',
    name: 'Address label stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'floral', label: 'Floral corner', subject: 'A rectangular label with a floral corner and three blank ruled lines'},
        {key: 'minimal', label: 'Minimal line', subject: 'A rectangular label with a single thin border and three blank ruled lines'},
        {key: 'house', label: 'House icon', subject: 'A rectangular label with a small house icon and three blank ruled lines'},
    ],
    styles: [
        {key: 'kiss', label: 'Kiss-cut sheet', style: 'kiss-cut sticker sheet, glossy stickers on a matte backing, offset white borders, cut lines drawn', palette: 'Mixed bright colours on a pale backing sheet', framing: 'Nine to twelve stickers arranged in rows on one sheet with a margin, a plain background between them'},
        {key: 'die', label: 'Die-cut set', style: 'die-cut sticker set, each shape cut to its own outline, white border', palette: 'One bright flat colour, no gradient', framing: 'The set laid out spaced apart on a plain ground'},
        {key: 'planner', label: 'Planner strip', style: 'planner sticker strip, small tidy icons in one pastel palette, cut lines drawn', palette: 'One soft pastel palette', framing: 'Small stickers in neat rows on a narrow strip with a margin, on a plain background'},
    ],
    directions: ['in a single accent colour', 'for a holiday card', 'as a round label instead'],
    directionHint: 'try: in a single accent colour',
    seo: {
        title: 'Address Label Stickers: Floral, Minimal, House Icon',
        metaDescription: 'Nine return address labels — a floral corner, a minimal line, a house icon — in kiss-cut, die-cut, and planner finishes.',
        h1: 'Address label stickers: floral, minimal, and house icon in three finishes',
        tagline: 'Nine address labels, three designs, three finishes each.',
        intro: 'The return address label for the corner of the envelope: a floral corner, a minimal line, a house icon, each with the address lines left blank so you fill in your own. Rendered kiss-cut, die-cut, and planner-strip to fit the sheet you print on. Ask for a different border motif, and the same three finishes carry it.',
        useCases: [
            'wedding invitation return addresses',
            'everyday mail return address labels',
            'a small business shipping label',
            'a housewarming or moving-announcement label',
            'a printable address label sheet',
        ],
        faq: [
            {q: 'Is my address already printed on the label?', a: 'No — the address lines are left blank so you can add your own text with your printer or a fine-tip pen.'},
            {q: 'What size labels do these print as?', a: 'The designs print at standard return-address-label size; resize on your printer settings for a smaller or larger sheet layout.'},
            {q: 'Can I get a different border, like a wreath or a monogram?', a: 'Yes — the direction field takes any border motif, rendered in the same three finishes.'},
            {q: 'Will the printed text stay legible for the mail carrier?', a: 'Yes — the blank address area is left clean and high-contrast so handwritten or printed text stays readable.'},
            {q: "Can I use these for a small business's shipping labels?", a: 'Yes, for personal and small-business use; check the license terms before reselling the label designs themselves.'},
        ],
    },
}
